'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_FEEDBACK_MS = 1500;

/**
 * Copies a text to the clipboard and raises `isCopied` for `feedbackMs`, for
 * a "Copied" confirmation next to a copy button.
 *
 * - A repeated copy restarts the window from the latest click, and the timer
 *   is cleared on unmount (no state update after the component is gone).
 * - Without a usable `navigator.clipboard` (insecure context, older browsers)
 *   or when the write is rejected, the copy is a no-op and `isCopied` stays
 *   false — the confirmation never claims a copy that did not happen. The
 *   failure is logged: the button itself gives no sign of it.
 */
export const useCopyWithFeedback = (feedbackMs = DEFAULT_FEEDBACK_MS) => {
  const [isCopied, setIsCopied] = useState(false);
  const resetRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The clipboard write is async: a component can unmount while it is pending.
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (resetRef.current !== null) clearTimeout(resetRef.current);
    };
  }, []);

  const copy = useCallback(
    async (text: string): Promise<void> => {
      if (
        typeof navigator === 'undefined' ||
        typeof navigator.clipboard?.writeText !== 'function'
      ) {
        console.error('Failed to copy: the Clipboard API is unavailable');
        return;
      }
      try {
        await navigator.clipboard.writeText(text);
      } catch (error) {
        console.error('Failed to copy:', error);
        return;
      }
      if (!isMountedRef.current) return;
      if (resetRef.current !== null) clearTimeout(resetRef.current);
      setIsCopied(true);
      resetRef.current = setTimeout(() => {
        setIsCopied(false);
        resetRef.current = null;
      }, feedbackMs);
    },
    [feedbackMs],
  );

  return { isCopied, copy };
};
